'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GeneralNotFound, Spinner, TaskViewer } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getTask } from 'src/api/admin/tasks';
import { getEventDocuments } from 'src/api/personal/eventDetail';

export const Route = createFileRoute('/personal_/events/$eventId/approvals/$taskId')({
	component: ApprovalDetailPage,
});

function ApprovalDetailPage() {
	const { eventId, taskId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);

	const { data: task, isLoading: taskLoading } = useQuery({
		queryKey: ['task', taskId],
		queryFn: () => getTask(taskId),
	});

	const { data: documentsData, isLoading: documentsLoading } = useQuery({
		queryKey: ['personal-event-documents', eventId, !hasAllEventsView],
		queryFn: () => getEventDocuments(eventId, !hasAllEventsView),
		enabled: !!task,
	});

	const handleClose = () => {
		navigate({ to: '/personal/events/$eventId/approvals', params: { eventId } });
	};

	const handleOpenDocument = (documentId: string) => {
		window.open(`/personal/events/${eventId}/documents/${documentId}`, '_blank');
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['task', taskId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event-approvals', eventId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
	};

	const isLoading = authLoading || taskLoading || documentsLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!task) {
		return (
			<GeneralNotFound
				label="We couldn't find this task. Maybe it has been deleted?"
				action={{
					label: 'Return to approvals',
					href: `/personal/events/${eventId}/approvals`,
					variant: 'outlined',
				}}
			/>
		);
	}

	const documents = (documentsData?.data || []).filter((d: { _id: string }) =>
		task.documentIds?.includes(d._id),
	);

	return (
		<TaskViewer
			task={task}
			documents={documents}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDelete: false,
				taskDo: task.executePermission?.isAllowed,
			}}
		/>
	);
}
